// signal-result-dialog.component.ts
import { Component, Inject, OnInit, ViewChild, ElementRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { JsignalService } from 'src/app/shared/services/jsignal.service';


@Component({
    selector: 'app-signal-result-dialog',
    templateUrl: './signal-result-dialog.component.html',
    styleUrls: ['./signal-result-dialog.component.scss'],
    standalone: false
})
export class SignalResultDialogComponent implements OnInit {

  originalRowData: any[] = [];
  filteredRowData: any[] = [];
  loading = true;
  isTableView = true;
  showPagination = true;
  pageSize = 25;

  // Filters
  drugFilter = '';
  eventFilter = '';
  minCaseCount: number | null = null;
  minPRR: number | null = null;

  private gridApi: any;
  
  // Chart data (simplified without ECharts)
  topSignals: any[] = [];
  caseDistribution: any[] = [];

  columnDefs = [
    { 
      headerName: "Drug", 
      field: "drug_name", 
      minWidth: 140,
      filter: 'agTextColumnFilter',
      tooltipField: 'drug_name'
    },
    { 
      headerName: "Event", 
      field: "event_term", 
      minWidth: 180,
      filter: 'agTextColumnFilter',
      tooltipField: 'event_term'
    },
    { 
      headerName: "Case Count", 
      field: "observed_count", 
      minWidth: 130,
      filter: 'agNumberColumnFilter',
      cellClass: 'text-right'
    },
    { 
      headerName: "PRR", 
      field: "prr", 
      minWidth: 100,
      filter: 'agNumberColumnFilter',
       valueFormatter: (params: any) => params.value?.toFixed(2)

    },
    { 
      headerName: "PRR CI Low", 
      field: "prr_ci_low", 
      minWidth: 100,
      valueFormatter: (params: any) => params.value?.toFixed(2)
    },
    { 
      headerName: "PRR CI High", 
      field: "prr_ci_high", 
      minWidth: 100,
      valueFormatter: (params: any) => params.value?.toFixed(2)
    },
    { 
      headerName: "ROR", 
      field: "ror", 
      minWidth: 100,
      valueFormatter: (params: any) => params.value?.toFixed(2)
    },
    { 
      headerName: "ROR CI Low", 
      field: "ror_ci_low", 
      minWidth: 100,
      valueFormatter: (params: any) => params.value?.toFixed(2)
    },
    { 
      headerName: "ROR CI High", 
      field: "ror_ci_high", 
      minWidth: 100,
      valueFormatter: (params: any) => params.value?.toFixed(2)
    },
    { 
      headerName: "Chi-Square", 
      field: "chi_square", 
      minWidth: 100,
      valueFormatter: (params: any) => params.value?.toFixed(2)
    },
    { 
      headerName: "IC", 
      field: "ic", 
      minWidth: 100,
      valueFormatter: (params: any) => params.value?.toFixed(2)
    },
    { 
      headerName: "IC025", 
      field: "ic025", 
      minWidth: 100,
      valueFormatter: (params: any) => params.value?.toFixed(2)
    },
    { 
      headerName: "IC975", 
      field: "ic975", 
      minWidth: 100,
      valueFormatter: (params: any) => params.value?.toFixed(2)
    },
    { 
      headerName: "EBGM", 
      field: "ebgm", 
      minWidth: 100,
      valueFormatter: (params: any) => params.value?.toFixed(2)
    },
    { 
      headerName: "EB05", 
      field: "eb05", 
      minWidth: 100,
      valueFormatter: (params: any) => params.value?.toFixed(2)
    },
    { 
      headerName: "EB95", 
      field: "eb95", 
      minWidth: 100,
      valueFormatter: (params: any) => params.value?.toFixed(2)
    }
  ];

  miniColumnDefs = [
    { headerName: "Drug", field: "drug_name", minWidth: 120 },
    { headerName: "Event", field: "event_term", minWidth: 150 },
    { headerName: "Case Count", field: "observed_count", width: 100, cellClass: 'text-right' },
    { 
      headerName: "PRR", 
      field: "prr", 
      width: 80,
      valueFormatter: (params: any) => params.value?.toFixed(2)
    }
  ];

  defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private jsignalsService: JsignalService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadResult();
  }

  loadResult() {
    this.loading = true;
    this.jsignalsService.getAnalysisResult(this.data.jobId).subscribe({
      next: (response: any) => {
        console.log('Raw response:', response);
        
        let dataArray = [];
        
        // Check if it's an HttpResponse object
        if (response && response.body) {
          dataArray = response.body;
        } 
        // Check if it's a direct array
        else if (response && Array.isArray(response)) {
          dataArray = response;
        }
        // Check if data is nested in a property
        else if (response && response.data && Array.isArray(response.data)) {
          dataArray = response.data;
        }
        else {
          dataArray = [];
        }
        
        // Ensure it's an array
        if (Array.isArray(dataArray)) {
          this.originalRowData = [...dataArray];
          this.filteredRowData = [...dataArray];
          this.updateCharts();
        } else {
          this.originalRowData = [];
          this.filteredRowData = [];
        }
        
        console.log('Final rowData:', this.filteredRowData);
        console.log('Row count:', this.filteredRowData.length);
        this.loading = false;
      },
      error: (err) => {
        console.error("Failed to load result", err);
        this.originalRowData = [];
        this.filteredRowData = [];
        this.loading = false;
      }
    });
  }

  applyFilters() {
    this.filteredRowData = this.originalRowData.filter(row => {
      // Drug filter
      if (this.drugFilter && !row.drug_name?.toLowerCase().includes(this.drugFilter.toLowerCase())) {
        return false;
      }
      // Event filter
      if (this.eventFilter && !row.event_term?.toLowerCase().includes(this.eventFilter.toLowerCase())) {
        return false;
      }
      // Case count filter
      if (this.minCaseCount && row.observed_count < this.minCaseCount) {
        return false;
      }
      // PRR filter
      if (this.minPRR && row.prr < this.minPRR) {
        return false;
      }
      return true;
    });
    
    this.updateCharts();
    if (this.gridApi) {
      this.gridApi.setGridOption('rowData', this.filteredRowData);
    }
  }

  clearAllFilters() {
    this.drugFilter = '';
    this.eventFilter = '';
    this.minCaseCount = null;
    this.minPRR = null;
    this.applyFilters();
  }

  hasActiveFilters(): boolean {
    return !!(this.drugFilter || this.eventFilter || this.minCaseCount || this.minPRR);
  }

  openAdvancedFilters() {
    // Implement advanced filters dialog
    // For now, show a simple alert or you can create a separate dialog
    alert('Advanced filters coming soon - You can filter by date range, multiple conditions, etc.');
  }

  getUniqueDrugs(): number {
    return new Set(this.filteredRowData.map(d => d.drug_name)).size;
  }

  getUniqueEvents(): number {
    return new Set(this.filteredRowData.map(d => d.event_term)).size;
  }

  getAvgPRR(): number {
    if (this.filteredRowData.length === 0) return 0;
    const sum = this.filteredRowData.reduce((acc, row) => acc + (row.prr || 0), 0);
    return sum / this.filteredRowData.length;
  }

  updateCharts() {
  // For single drug result, show PRR comparison across different metrics
  if (this.filteredRowData.length === 1) {
    const drug = this.filteredRowData[0];
    
    // Create metrics comparison for the single drug
    this.topSignals = [
      { metric: 'PRR', value: drug.prr, ci_low: drug.prr_ci_low, ci_high: drug.prr_ci_high },
      { metric: 'ROR', value: drug.ror, ci_low: drug.ror_ci_low, ci_high: drug.ror_ci_high },
      { metric: 'EBGM', value: drug.ebgm, ci_low: drug.eb05, ci_high: drug.eb95 },
      { metric: 'IC', value: drug.ic, ci_low: drug.ic025, ci_high: drug.ic975 },
      { metric: 'RR', value: drug.rr, ci_low: null, ci_high: null }
    ].filter(m => m.value !== null && m.value !== undefined);
    
    // For case count, show single value distribution
    this.caseDistribution = [
      { range: 'Observed Cases', count: drug.observed_count, type: 'observed' },
      { range: 'Expected Cases', count: drug.expected_count, type: 'expected' }
    ];
  } 
  // For multiple drugs, show top signals by PRR
  else {
    this.topSignals = [...this.filteredRowData]
      .sort((a, b) => (b.prr || 0) - (a.prr || 0))
      .slice(0, 10)
      .map(drug => ({
        drug_name: drug.drug_name,
        event_term: drug.event_term,
        prr: drug.prr,
        observed_count: drug.observed_count
      }));
    
    // Case count distribution across multiple drugs
    const caseRanges = { '<10': 0, '10-50': 0, '50-100': 0, '100-500': 0, '>500': 0 };
    this.filteredRowData.forEach(row => {
      const count = row.observed_count;
      if (count < 10) caseRanges['<10']++;
      else if (count < 50) caseRanges['10-50']++;
      else if (count < 100) caseRanges['50-100']++;
      else if (count < 500) caseRanges['100-500']++;
      else caseRanges['>500']++;
    });
    
    this.caseDistribution = Object.entries(caseRanges).map(([range, count]) => ({ range, count }));
  }
}
// Add this method to your component
getMaxMetricValue(): number {
  if (!this.topSignals || this.topSignals.length === 0) return 1;
  return Math.max(...this.topSignals.map(m => m.value));
}

getTotalForDistribution(): number {
  if (!this.caseDistribution || this.caseDistribution.length === 0) return 1;
  return this.caseDistribution.reduce((sum, item) => sum + item.count, 0);
}

  viewChartFullscreen(chartType: string) {
    // Implement fullscreen view for charts
    console.log('View fullscreen chart:', chartType);
    // You can open a dialog with enlarged chart here
  }

  onGridReady(params: any) {
    this.gridApi = params.api;
    params.api.sizeColumnsToFit();
  }

  onQuickFilter(event: any) {
    this.gridApi?.setGridOption('quickFilterText',event.target.value);
  }

  onPageSizeChange() {
    if (this.gridApi) {
      this.gridApi.setGridOption('paginationPageSize', this.pageSize);
    }
  }

  exportToCSV() {
    this.gridApi?.exportDataAsCsv({
      fileName: `analysis_${this.data.jobId}.csv`
    });
  }



  toggleView() {
    this.isTableView = !this.isTableView;
  }

  refreshData() {
    this.loadResult();
  }

  exportData() {
    this.exportToCSV();
  }
}